/**
 * SCRAPING SERVICE - Sistema de extracción de datos de Amazon
 * Mantiene compatibilidad con la interfaz existente
 */

class KaggleService {
  constructor() {
    this.connected = false;
    this.credentials = null;
    this.lastError = null;
    this.currentDatasets = [];
    this.API_BASE_URL = 'https://mercadobulk.com';
  }

  // Activa sistema de scraping directo
  async connect(credentials) {
    try {
      console.log('🔄 Activando sistema de scraping de Amazon...');
      this.connected = true;
      this.credentials = {
        username: 'scraper_user',
        validated: true,
        connectedAt: new Date().toISOString()
      };
      this.lastError = null;
      
      return {
        success: true,
        message: 'Sistema de scraping activado exitosamente',
        data: {
          username: 'scraper_user',
          connected: true,
          validated: true
        }
      };
    } catch (error) {
      console.error('❌ Error activando scraper:', error);
      this.connected = false;
      this.lastError = error.message;
      throw error;
    }
  }

  // Inicia scraping REAL en el backend
  async searchDatasets(query = 'products', page = 1, pageSize = 100, category = 'general') {
    try {
      // AUTO-ACTIVAR el sistema si no está conectado
      if (!this.connected) {
        console.log('🔄 Auto-activando sistema de scraping...');
        await this.connect({ auto: true });
      }

      console.log(`🔍 Iniciando scraping REAL de Amazon para: "${query}" en categoría: "${category}"`);

      // Hacer llamada REAL al backend de scraping
      const response = await fetch('/api/scrape/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: 'amazon',
          mode: 'search',
          query: query,
          category: category,
          maxItems: pageSize,
          jobId: `scrape-${Date.now()}`
        })
      });

      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error iniciando scraping');
      }

      // Convertir respuesta del backend a formato compatible
      const scrapingJobs = [{
        id: result.data.jobId,
        title: `Scraping Amazon - ${category}: ${query}`,
        description: `Extrayendo productos de Amazon categoría ${category} para: ${query}`,
        size: `${result.data.maxItems} productos máximo`,
        downloadCount: 0,
        voteCount: 0,
        lastUpdated: new Date().toISOString(),
        tags: ['amazon', 'live-scraping', category, query],
        url: result.data.targetUrl || `https://amazon.com/s?k=${encodeURIComponent(query)}`,
        scrapingConfig: {
          source: 'amazon',
          category: category,
          mode: 'search',
          query: query,
          maxItems: result.data.maxItems,
          jobId: result.data.jobId,
          status: result.data.status
        }
      }];

      this.currentDatasets = scrapingJobs;
      
      return {
        success: true,
        message: `Scraping iniciado: Job ${result.data.jobId} - ${category}`,
        data: {
          query,
          category,
          datasets: scrapingJobs,
          total: scrapingJobs.length,
          page: parseInt(page),
          pageSize: parseInt(pageSize),
          realScraping: true,
          jobStatus: result.data.status
        }
      };

    } catch (error) {
      console.error('❌ Error generando jobs de scraping:', error);
      this.lastError = error.message;
      throw error;
    }
  }

  // Descarga dataset -> inicia scraping job
  async downloadDataset(datasetId) {
    try {
      if (!this.connected) {
        throw new Error('Sistema de scraping no activado');
      }

      console.log(`📥 Iniciando scraping job: ${datasetId}`);

      // Buscar configuración del job
      const dataset = this.currentDatasets.find(d => d.id === datasetId);
      if (!dataset) {
        throw new Error(`Job de scraping no encontrado: ${datasetId}`);
      }

      // Iniciar scraping REAL
      const response = await fetch('/api/scrape/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: dataset.scrapingConfig.source,
          mode: dataset.scrapingConfig.mode,
          query: dataset.scrapingConfig.query,
          category: dataset.scrapingConfig.category,
          maxItems: dataset.scrapingConfig.maxItems,
          jobId: datasetId
        })
      });

      const result = await response.json();

      if (result.success) {
        return {
          success: true,
          message: 'Job de scraping iniciado exitosamente',
          data: {
            datasetId,
            jobId: result.jobId,
            extractDir: result.outputPath,
            files: [{
              name: 'scraped_products.json',
              path: `${result.outputPath}/parsed/products.json`,
              size: 0 // Se actualizará durante el scraping
            }],
            totalFiles: 1,
            scrapingStatus: 'started'
          }
        };
      } else {
        throw new Error(result.message || 'Error iniciando scraping');
      }

    } catch (error) {
      console.error('❌ Error iniciando scraping:', error);
      this.lastError = error.message;
      throw error;
    }
  }

  // Convierte dataset -> convierte datos scrapeados
  async downloadAndConvert(datasetId, progressCallback = null) {
    try {
      const steps = [
        { step: 1, total: 5, message: 'Iniciando scraping en tiempo real...' },
        { step: 2, total: 5, message: 'Extrayendo productos desde marketplace...' },
        { step: 3, total: 5, message: 'Validando datos obtenidos...' },
        { step: 4, total: 5, message: 'Convirtiendo a formato MercadoLibre...' },
        { step: 5, total: 5, message: 'Generando Excel para publicación...' }
      ];

      // Paso 1: Verificar conexión
      if (progressCallback) progressCallback(steps[0]);
      if (!this.connected) {
        throw new Error('Sistema de scraping no activado');
      }

      // Paso 2: Iniciar scraping
      if (progressCallback) progressCallback(steps[1]);
      const downloadResult = await this.downloadDataset(datasetId);

      // Paso 3: Esperar que complete el scraping
      if (progressCallback) progressCallback(steps[2]);
      await this.waitForScrapingCompletion(downloadResult.data.jobId, progressCallback);

      // Paso 4-5: Convertir a MercadoLibre
      if (progressCallback) progressCallback(steps[3]);
      const convertResult = await this.convertToMercadoLibre(
        downloadResult.data.extractDir,
        downloadResult.data.files
      );

      if (progressCallback) progressCallback(steps[4]);
      
      return {
        success: true,
        data: {
          originalDataset: downloadResult.data,
          convertedFile: convertResult.data,
          summary: {
            datasetId,
            recordsProcessed: convertResult.data.recordsProcessed,
            excelFile: convertResult.data.excelFile,
            downloadUrl: convertResult.data.downloadUrl,
            categories: convertResult.data.summary?.categories || [],
            avgPrice: convertResult.data.summary?.avgPrice || 0
          }
        }
      };

    } catch (error) {
      console.error('❌ Error en proceso completo de scraping:', error);
      this.lastError = error.message;
      throw error;
    }
  }

  // Método auxiliar para esperar completación
  async waitForScrapingCompletion(jobId, progressCallback) {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutos máximo

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`/api/scrape/status/${jobId}`);
        const status = await response.json();
        
        if (status.state === 'completed') {
          console.log(`✅ Scraping completado: ${status.itemsScraped} productos`);
          return status;
        } else if (status.state === 'failed') {
          throw new Error(`Scraping falló: ${status.error}`);
        }
        
        // Actualizar progreso si hay callback
        if (progressCallback && status.progress) {
          progressCallback({
            step: 2,
            total: 5,
            message: `Scrapeando... ${status.itemsScraped}/${status.maxItems} productos`
          });
        }
        
        await new Promise(resolve => setTimeout(resolve, 5000)); // Esperar 5 segundos
        attempts++;
        
      } catch (error) {
        if (attempts >= maxAttempts - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 5000));
        attempts++;
      }
    }
    
    throw new Error('Timeout esperando completación del scraping');
  }

  // Conversión usando el nuevo sistema
  async convertToMercadoLibre(extractDir, files) {
    try {
      console.log(`🔄 Convirtiendo datos scrapeados a formato MercadoLibre...`);

      const response = await fetch(`${this.API_BASE_URL}/api/convert-to-mercadolibre`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ extractDir, files })
      });

      const result = await response.json();

      if (result.success) {
        console.log(`✅ Conversión completada: ${result.data.recordsProcessed} productos procesados`);
        return result;
      } else {
        throw new Error(result.message || 'Error convirtiendo datos scrapeados');
      }

    } catch (error) {
      console.error('❌ Error convirtiendo datos scrapeados:', error);
      this.lastError = error.message;
      throw error;
    }
  }

  // Descargar Excel generado
  async downloadExcelFile(fileName) {
    try {
      const url = `${this.API_BASE_URL}/api/download-excel/${fileName}`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`✅ Descarga de Excel iniciada: ${fileName}`);
      return { success: true, fileName };

    } catch (error) {
      console.error('❌ Error descargando Excel:', error);
      this.lastError = error.message;
      throw error;
    }
  }

  getConnectionStatus() {
    return {
      connected: this.connected,
      credentials: this.credentials,
      lastError: this.lastError,
      availableDatasets: this.currentDatasets.length
    };
  }

  disconnect() {
    this.connected = false;
    this.credentials = null;
    this.lastError = null;
    this.currentDatasets = [];
    console.log('🔌 Sistema de scraping desactivado');
  }

  // Método estático para validación (compatibilidad)
  static validateCredentialsFile(file) {
    return new Promise((resolve) => {
      // Simula validación exitosa
      resolve({
        valid: true,
        username: 'scraper_user',
        content: { username: 'scraper_user', key: 'scraper_activated' },
        fileSize: file.size
      });
    });
  }

  // Información de jobs disponibles
  static getPopularAmazonDatasets() {
    return [
      {
        id: 'amazon-bestsellers-electronics',
        title: 'Amazon Best Sellers - Electronics',
        description: 'Scraping en tiempo real de productos más vendidos en electrónicos',
        estimatedSize: 'Variable (scraped live)',
        estimatedRecords: '1,000+',
        lastUpdated: new Date().toISOString(),
        compatibility: '🟢 Alta - Datos extraídos en tiempo real',
        features: ['Precios actuales', 'Stock disponible', 'Reviews', 'Imágenes', 'Especificaciones']
      },
      {
        id: 'amazon-search-products',
        title: 'Amazon Search Results',
        description: 'Scraping de resultados de búsqueda personalizada en Amazon',
        estimatedSize: 'Variable (scraped live)',
        estimatedRecords: '2,000+',
        lastUpdated: new Date().toISOString(),
        compatibility: '🟢 Alta - Búsqueda personalizable',
        features: ['Filtros avanzados', 'Múltiples categorías', 'Datos actualizados']
      },
      {
        id: 'amazon-category-home-kitchen',
        title: 'Amazon Home & Kitchen',
        description: 'Scraping de productos de hogar y cocina con datos completos',
        estimatedSize: 'Variable (scraped live)',
        estimatedRecords: '1,500+',
        lastUpdated: new Date().toISOString(),
        compatibility: '🟢 Alta - Categoría específica',
        features: ['Atributos detallados', 'Comparativa de precios', 'Disponibilidad']
      }
    ];
  }

  static getScrapingInstructions() {
    return {
      title: 'Cómo activar el sistema de scraping',
      steps: [
        {
          step: 1,
          title: 'Activar sistema',
          description: 'Haga clic en "Activar Scraping" para comenzar la extracción de productos'
        },
        {
          step: 2,
          title: 'Seleccionar job de scraping',
          description: 'Elija el tipo de productos que desea scrapear de Amazon'
        },
        {
          step: 3,
          title: 'Iniciar extracción',
          description: 'El sistema extraerá datos en tiempo real desde Amazon'
        },
        {
          step: 4,
          title: 'Convertir a MercadoLibre',
          description: 'Los datos se convertirán automáticamente al formato de MercadoLibre'
        }
      ],
      important: [
        'Los datos se extraen en tiempo real desde Amazon',
        'No requiere credenciales externas - sistema completamente integrado',
        'Incluye rotación de proxies y límites de velocidad automáticos'
      ]
    };
  }
}

// Crear instancia singleton
const kaggleService = new KaggleService();

// Exportar tanto la instancia como la clase
export { KaggleService };
export default kaggleService;